// billing 에이전트 로컬 컴포넌트 라이브 샘플 — 실제 컴포넌트를 딥 경로로 import 해 목 데이터로 렌더한다.
import { useCallback, useState, type ReactNode } from 'react';
import { Button } from '@indipia/ui-core';
import type { PaymentMethod, PricingTier } from '@indipia/agents-billing/src/api/billingApi';
import { TIER_COPY } from '@indipia/agents-billing/src/constants/tierCopy';
import { PaymentMethodCardBody } from '@indipia/agents-billing/src/components/PaymentMethodCardBody';
import { PaymentMethodTile } from '@indipia/agents-billing/src/components/PaymentMethodTile';
import { PricingCard } from '@indipia/agents-billing/src/components/PricingCard';
import { ReasonSelectModal, type ReasonOption } from '@indipia/agents-billing/src/components/ReasonSelectModal';

const CARD: PaymentMethod = {
  paymentMethodId: 1,
  methodType: 'CARD',
  cardIssuer: '33',
  cardIssuerName: '우리카드',
  cardLast4: '0052',
  cardOwnerType: '법인',
  cardType: '신용',
  bankName: null,
  bankAccountLast4: null,
  cardAlias: '회사 법인카드',
  isDefault: true,
  registrationStatus: 'COMPLETED',
};

const CARD2: PaymentMethod = {
  ...CARD,
  paymentMethodId: 2,
  cardIssuerName: '신한카드',
  cardLast4: '7781',
  cardOwnerType: '개인',
  cardType: '체크',
  cardAlias: null,
  isDefault: false,
};

const STARTER: PricingTier = {
  tierCode: 'STARTER',
  monthlyPrice: 19000,
  annualMonthlyDisplay: 15800,
  annualTotal: 190000,
  maxConcurrentSiteLabel: '3개',
};

const REASONS: ReasonOption[] = [
  { code: 'PRICE', label: '가격이 부담돼요' },
  { code: 'UNUSED', label: '자주 사용하지 않아요' },
  { code: 'ETC', label: '기타' },
];
const fetchReasons = async (): Promise<ReasonOption[]> => REASONS;
const noop = () => {};

function PaymentMethodTileDemo() {
  const [selectedId, setSelectedId] = useState(1);
  const [methods, setMethods] = useState([CARD, CARD2]);
  const patch = (id: number, fn: (m: PaymentMethod) => PaymentMethod) =>
    setMethods((ms) => ms.map((m) => (m.paymentMethodId === id ? fn(m) : m)));
  return (
    <div className="flex gap-3">
      {methods.map((m) => (
        <PaymentMethodTile
          key={m.paymentMethodId}
          method={m}
          selected={selectedId === m.paymentMethodId}
          onSelect={() => setSelectedId(m.paymentMethodId)}
          onSetDefault={() =>
            setMethods((ms) => ms.map((x) => ({ ...x, isDefault: x.paymentMethodId === m.paymentMethodId })))
          }
          onDelete={noop}
          onSaveAlias={(cardAlias) => patch(m.paymentMethodId, (x) => ({ ...x, cardAlias: cardAlias || null }))}
          settingDefault={false}
          savingAlias={false}
        />
      ))}
    </div>
  );
}

function ReasonSelectModalDemo() {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        열기
      </Button>
      <ReasonSelectModal
        open={open}
        submitting={false}
        onClose={close}
        onConfirm={close}
        title="해지 사유를 알려주세요"
        selectLabel="해지 사유"
        etcCode="ETC"
        confirmLabel="해지하기"
        confirmVariant="danger"
        queryKey={['agent-components-demo', 'cancel-reasons']}
        fetchReasons={fetchReasons}
        reasonTextMaxLength={200}
        footerNote="해지해도 이번 결제 주기가 끝날 때까지 이용할 수 있어요."
      />
    </>
  );
}

export const demos: Record<string, () => ReactNode> = {
  PaymentMethodCardBody: () => (
    <div className="flex gap-6">
      <div>
        <PaymentMethodCardBody method={CARD} />
      </div>
      <div>
        <PaymentMethodCardBody method={CARD2} compact />
      </div>
    </div>
  ),
  PaymentMethodTile: () => <PaymentMethodTileDemo />,
  PricingCard: () => (
    <div className="w-72">
      <PricingCard
        tier={STARTER}
        copy={TIER_COPY.STARTER}
        billingCycle="annual"
        freeMonthsAnnual={2}
        state="upgrade"
        hasEverPaid={false}
        isTrialing={false}
        trialMonths={1}
        onCtaClick={noop}
      />
    </div>
  ),
  ReasonSelectModal: () => <ReasonSelectModalDemo />,
};
